import {
    Editor,
    EditorState,
    convertToRaw,
    ContentState,
    Modifier,
    SelectionState,
    AtomicBlockUtils
} from "draft-js";
import { getSelectedBlock } from "draftjs-utils";
import htmlToDraft from "html-to-draftjs";

import * as CommonAction from "../../actions/commonAction";
import UserInfoStore from "../../stores/userInfoStores";

export const fileObjToBase64 = file =>
    new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.readAsDataURL(file);
        reader.onload = () => resolve(reader.result);
        reader.onerror = error => reject(error);
    });

export const fileObjToS3 = fileObj => {
    let file = fileObj[0];

    const fileSize = file.size / (1024 * 1024);

    if (fileSize > 5) {
        UserInfoStore.showToast("Maximum allowed size for file upload at once is 5MB");
        return 0;
    }

    const reqData = new FormData();
    reqData.append("files", file, file.name);
    CommonAction.generateS3Url(reqData, "multipart/form-data");
};

export const validateUrl = value => {
    return /^(?:(?:(?:https?|ftp):)?\/\/)(?:\S+(?::\S*)?@)?(?:(?!(?:10|127)(?:\.\d{1,3}){3})(?!(?:169\.254|192\.168)(?:\.\d{1,3}){2})(?!172\.(?:1[6-9]|2\d|3[0-1])(?:\.\d{1,3}){2})(?:[1-9]\d?|1\d\d|2[01]\d|22[0-3])(?:\.(?:1?\d{1,2}|2[0-4]\d|25[0-5])){2}(?:\.(?:[1-9]\d?|1\d\d|2[0-4]\d|25[0-4]))|(?:(?:[a-z\u00a1-\uffff0-9]-*)*[a-z\u00a1-\uffff0-9]+)(?:\.(?:[a-z\u00a1-\uffff0-9]-*)*[a-z\u00a1-\uffff0-9]+)*(?:\.(?:[a-z\u00a1-\uffff]{2,})))(?::\d{2,5})?(?:[/?#]\S*)?$/i.test(
        value
    );
};

export const handlePasteLink = editorState => {
    // const RE_URL = new RegExp(
    //     /(http(s)?:\/\/.)?(www\.)?[-a-zA-Z0-9@:%._\+~#=]{2,256}\.[a-z]{2,6}\b([-a-zA-Z0-9@:%_\+.~#?&//=]*)/g
    // );

    const RE_URL = new RegExp(
        /^(?:(?:(?:https?|ftp):)?\/\/)(?:\S+(?::\S*)?@)?(?:(?!(?:10|127)(?:\.\d{1,3}){3})(?!(?:169\.254|192\.168)(?:\.\d{1,3}){2})(?!172\.(?:1[6-9]|2\d|3[0-1])(?:\.\d{1,3}){2})(?:[1-9]\d?|1\d\d|2[01]\d|22[0-3])(?:\.(?:1?\d{1,2}|2[0-4]\d|25[0-5])){2}(?:\.(?:[1-9]\d?|1\d\d|2[0-4]\d|25[0-4]))|(?:(?:[a-z\u00a1-\uffff0-9]-*)*[a-z\u00a1-\uffff0-9]+)(?:\.(?:[a-z\u00a1-\uffff0-9]-*)*[a-z\u00a1-\uffff0-9]+)*(?:\.(?:[a-z\u00a1-\uffff]{2,})))(?::\d{2,5})?(?:[/?#]\S*)?$/g
    );

    editorState
        .getCurrentContent()
        .getBlockMap()
        .map(block => {
            let contentState = editorState.getCurrentContent();
            const blockKey = block.getKey();
            const blockText = block.getText();

            let match = null;
            while ((match = RE_URL.exec(blockText))) {
                const start = match.index;
                const entityKeyStart = block.getEntityAt(start);
                if (!entityKeyStart) {
                    contentState = contentState.createEntity("LINK", "MUTABLE", {
                        href: match[0],
                        url: match[0],
                        targetOption: "_blank"
                    });
                    const entityKey = contentState.getLastCreatedEntityKey();
                    const contentStateWithEntity = Modifier.applyEntity(
                        contentState,
                        new SelectionState({
                            anchorKey: blockKey,
                            anchorOffset: start,
                            focusKey: blockKey,
                            focusOffset: match.index + match[0].length
                        }),
                        entityKey
                    );
                    editorState = EditorState.push(
                        editorState,
                        contentStateWithEntity,
                        "apply-entity"
                    );
                }
            }
        });
    return editorState;
};

export const renderCustomContent = (s3Url, fileName, editorState) => {
    fileName = fileName || "file_1";
    let extArr = ["png", "jpg", "jpeg"];
    let isImg = false;
    for (let i of extArr) {
        if (s3Url.includes(i)) {
            isImg = true;
        }
    }
    if (!isImg) {
        let data = `<a href=${s3Url} target="_blank">${fileName}</a>`;

        let { contentBlocks, entityMap } = htmlToDraft(data);
        const contentState = Modifier.replaceWithFragment(
            editorState.getCurrentContent(),
            editorState.getSelection(),
            ContentState.createFromBlockArray(contentBlocks, entityMap).getBlockMap()
        );

        return EditorState.push(editorState, contentState, "insert-fragment");
    } else {
        return insertImage(editorState, s3Url);
    }
};

export const insertImage = (editorState, url) => {
    const contentState = editorState.getCurrentContent();
    const contentStateWithEntity = contentState.createEntity("IMAGE", "IMMUTABLE", {
        src: url,
        alt: "Unable to display image. Please update Happay app!",
        "max-width": "375px",
        "max-height": "200px"
    });
    const entityKey = contentStateWithEntity.getLastCreatedEntityKey();
    const newEditorState = EditorState.set(editorState, {
        currentContent: contentStateWithEntity
    });
    return AtomicBlockUtils.insertAtomicBlock(newEditorState, entityKey, " ");
};

export const handlePastedText = (text, html, editorState, onChange) => {
    const selectedBlock = getSelectedBlock(editorState);
    if (selectedBlock && selectedBlock.type === "code") {
        const contentState = Modifier.replaceText(
            editorState.getCurrentContent(),
            editorState.getSelection(),
            text,
            editorState.getCurrentInlineStyle()
        );
        onChange(EditorState.push(editorState, contentState, "insert-characters"));
        return true;
    }
    // else if (html) {
    //     const blockMap = stateFromHTML(html).blockMap;
    //     const newState = Modifier.replaceWithFragment(
    //         editorState.getCurrentContent(),
    //         editorState.getSelection(),
    //         blockMap
    //     );
    //     onChange(EditorState.push(editorState, newState, "insert-fragment"));
    //     return true;
    // }
    return false;
};
